import {createBindingId, createShapeId, getArrowBindings} from 'tldraw';
import {
    ENTITY_CARD_MAX_HEIGHT,
    ENTITY_CARD_SHAPE_TYPE,
    ENTITY_CARD_WIDTH,
} from '../tldraw/customShapes/entityCardConstants';
import {createEntityCardShape, getCenteredEntityCardPosition} from '../tldraw/customShapes/entityCardUtils';
import {collectChartEntitiesFromEditor} from '../utils/collectChartEntities';
import {
    anchorTFromNormalized,
    arrowStrokeWidth,
    buildArcPolyline,
    buildElbowPolyline,
    countCardInteriorEdgeHits,
    facingSide,
    preferredEnterSide,
    fanInSourceSortKey,
    normalizedAnchorForSide,
    rectCenter,
    spreadNormalizedAnchorsForSide,
    preferredFanInBendSign,
    countNodeHits,
    pickArcArrowRoute,
    pickBestArrowRoute,
    pickBestCardCenter,
    pickBestStraightRoute,
    pickRoutedArrowRoute,
    pickStraightOrArcArrowRoute,
    pointAlongPolyline,
    sideFromNormalizedAnchor,
    estimateMinEdgeLengthForLabel,
} from './chartArrowRouting';
import {collisionLabelBoxAt, placementLabelSize} from './edgeLabelPlacement';
import {
    isBareQidLabel,
    resolveMutationEntityLabel,
    sanitizeArrowRelationLabel,
} from './mutationEntityLabel';
import {hydrateEntityCardsFromKb} from '../utils/entityCardKbHydration';
import {formatChartErrorForUser} from './chartUserMessages';
import {layoutChartHubSquare} from './compactChartLayout';
import {isChartSpreadEntryPointsEnabled} from './featureFlags';
import {AI_AUTHOR_UID, applyMachineAuthorshipToShape, mergeAuthorshipMeta} from './chartAuthorship';

export {
    resolveMutationEntityLabel,
    sanitizeArrowRelationLabel,
} from './mutationEntityLabel';

const UNRESOLVED_ENTITY_LABEL = 'Unnamed entity';
const GRID_GAP = 56;
const GRID_COLS = 3;
const MAX_HIERARCHY_ROW_WIDTH = 4;
/** Reserve max card height so KB hydration cannot create overlap. */
const PLACEMENT_CARD_HEIGHT = ENTITY_CARD_MAX_HEIGHT;
const COLLISION_PAD = 36;
const MAX_PLACEMENT_ATTEMPTS = 48;

/**
 * Build entityRef → shapeId map from current page entity-cards.
 * @param {import('tldraw').Editor} editor
 * @returns {Map<string, string>}
 */
export function normalizeEntityRef(ref) {
    if (ref == null) return ref;
    const trimmed = String(ref).trim();
    if (/^q\d+$/i.test(trimmed)) return trimmed.toUpperCase();
    return trimmed;
}

export function indexEntityShapesByRef(editor) {
    const map = new Map();
    for (const entity of collectChartEntitiesFromEditor(editor)) {
        if (entity.wikidataId && entity.shapeId) {
            map.set(normalizeEntityRef(entity.wikidataId), entity.shapeId);
        }
    }
    return map;
}

/**
 * Resolve a shape id for an entity ref, optionally preferring a caller-supplied card.
 *
 * @param {import('tldraw').Editor} editor
 * @param {string} entityRef
 * @param {Map<string, string>} entityShapeIds
 * @param {string|null|undefined} preferredShapeId
 * @returns {string|null}
 */
export function resolveEntityShapeId(editor, entityRef, entityShapeIds, preferredShapeId) {
    const ref = normalizeEntityRef(entityRef);
    if (!ref) return null;
    if (preferredShapeId) {
        const shape = editor.getShape?.(preferredShapeId);
        if (
            shape?.type === ENTITY_CARD_SHAPE_TYPE
            && normalizeEntityRef(shape.props?.wikidataId) === ref
        ) {
            return preferredShapeId;
        }
    }
    return entityShapeIds.get(ref) || null;
}

/**
 * Relation label for a planned card add, inferred from a sibling add_arrow mutation.
 *
 * @param {object} cardMutation
 * @param {Array<object>|null|undefined} mutations
 * @returns {string|null}
 */
export function findPlannedEdgeLabelForCard(cardMutation, mutations) {
    const entityRef = normalizeEntityRef(cardMutation?.entityRef);
    if (!entityRef) return sanitizeArrowRelationLabel(cardMutation?.edgeLabel) || null;

    const explicit = sanitizeArrowRelationLabel(cardMutation?.edgeLabel);
    if (explicit) return explicit;

    for (const mutation of mutations || []) {
        if (mutation?.op !== 'add_arrow') continue;
        const fromRef = normalizeEntityRef(mutation.fromEntityRef);
        const toRef = normalizeEntityRef(mutation.toEntityRef);
        if (fromRef !== entityRef && toRef !== entityRef) continue;
        const label = sanitizeArrowRelationLabel(mutation.label || mutation.relation);
        if (label) return label;
    }
    return null;
}

/**
 * Collect human labels from the whole plan (add cards + arrow endpoint labels).
 * @param {Array<object>} mutations
 * @returns {Map<string, string>}
 */
export function collectPlanLabelHints(mutations) {
    const hints = new Map();
    const remember = (ref, label) => {
        if (!ref || !label) return;
        const resolved = resolveMutationEntityLabel({id: ref, wikidataId: ref, label});
        // Skip machine ids / Unnamed fallback so a later arrow fromLabel can win.
        if (!resolved || isBareQidLabel(resolved, ref) || resolved === UNRESOLVED_ENTITY_LABEL) {
            return;
        }
        const existing = hints.get(ref);
        if (!existing
            || isBareQidLabel(existing, ref)
            || existing === UNRESOLVED_ENTITY_LABEL) {
            hints.set(ref, resolved);
        }
    };
    for (const mutation of mutations || []) {
        if (mutation?.op === 'add_entity_card' && mutation.entityRef) {
            remember(mutation.entityRef, mutation.label);
        }
        if (mutation?.op === 'update_entity_card' && mutation.entityRef) {
            remember(mutation.entityRef, mutation.label);
        }
        if (mutation?.op === 'add_arrow') {
            remember(mutation.fromEntityRef, mutation.fromLabel);
            remember(mutation.toEntityRef, mutation.toLabel);
        }
    }
    return hints;
}

/**
 * Page-space AABB for a shape, using card placement height as a floor.
 * @param {import('tldraw').Editor} editor
 * @param {string} shapeId
 * @returns {{x: number, y: number, w: number, h: number}|null}
 */
export function shapeRect(editor, shapeId) {
    const shape = editor.getShape?.(shapeId);
    const pageBounds = editor.getShapePageBounds?.(shapeId);
    if (pageBounds) {
        const width = pageBounds.w
            ?? (Number.isFinite(pageBounds.maxX) ? pageBounds.maxX - pageBounds.x : ENTITY_CARD_WIDTH);
        return {
            x: pageBounds.x,
            y: pageBounds.y,
            w: width || ENTITY_CARD_WIDTH,
            h: Math.max(pageBounds.h || 0, shape?.props?.h || 0),
        };
    }
    if (!shape) return null;
    return {
        x: shape.x,
        y: shape.y,
        w: shape.props?.w || ENTITY_CARD_WIDTH,
        h: Math.max(shape.props?.h || 0, 80),
    };
}

/**
 * Approximate existing arrows as elbow or sampled-arc polylines for crossing tests.
 * Unbound style-sample arrows are ignored.
 *
 * @param {import('tldraw').Editor} editor
 * @param {Set<string>|null} [excludeShapeIds]
 * @returns {Array<{
 *   points: Array<{x: number, y: number}>,
 *   labelPoint: {x: number, y: number},
 *   labelBox: {x: number, y: number, w: number, h: number}|null,
 * }>}
 */
function arrowPolylineForShape(editor, shape) {
    const bindings = getArrowBindings(editor, shape);
    const startId = bindings?.start?.toId;
    const endId = bindings?.end?.toId;
    if (!startId || !endId) return null;
    const fromRect = shapeRect(editor, startId);
    const toRect = shapeRect(editor, endId);
    if (!fromRect || !toRect) return null;
    const fromSide = sideFromNormalizedAnchor(bindings.start?.props?.normalizedAnchor)
        || facingSide(fromRect, toRect);
    const toSide = sideFromNormalizedAnchor(bindings.end?.props?.normalizedAnchor)
        || facingSide(toRect, fromRect);
    const fromT = anchorTFromNormalized(bindings.start?.props?.normalizedAnchor);
    const toT = anchorTFromNormalized(bindings.end?.props?.normalizedAnchor);
    const points = shape.props?.kind === 'arc'
        ? buildArcPolyline(fromRect, toRect, {
            fromSide,
            toSide,
            fromT,
            toT,
            bend: shape.props?.bend ?? 40,
        })
        : buildElbowPolyline(fromRect, toRect, {
            fromSide,
            toSide,
            fromT,
            toT,
            midPoint: shape.props?.elbowMidPoint ?? 0.5,
        });
    const labelPoint = pointAlongPolyline(points, shape.props?.labelPosition ?? 0.5);
    const label = arrowLabelTextFromShape(shape);
    return {
        points,
        startId,
        endId,
        labelPoint,
        labelBox: label ? collisionLabelBoxAt(labelPoint, placementLabelSize(label)) : null,
    };
}

export function collectExistingEdgePolylines(editor, excludeShapeIds = null) {
    const polylines = [];
    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        if (excludeShapeIds?.has(shape.id)) continue;
        const poly = arrowPolylineForShape(editor, shape);
        if (!poly) continue;
        polylines.push({
            points: poly.points,
            labelPoint: poly.labelPoint,
            labelBox: poly.labelBox,
            strokeWidth: arrowStrokeWidth(shape.props?.size),
        });
    }
    return polylines;
}

function sameCardRect(a, b) {
    if (!a || !b) return false;
    const eps = 2;
    return Math.abs(a.x - b.x) < eps
        && Math.abs(a.y - b.y) < eps
        && Math.abs(a.w - b.w) < eps
        && Math.abs(a.h - b.h) < eps;
}

/** Max foreign-card hits across all arrow polylines (0 = no edge runs through another card). */
export function measureMaxForeignEdgeCardHits(editor) {
    let maxHits = 0;
    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        const poly = arrowPolylineForShape(editor, shape);
        if (!poly?.points?.length) continue;
        const fromRect = shapeRect(editor, poly.startId);
        const toRect = shapeRect(editor, poly.endId);
        const foreign = collectEntityCardBounds(editor).filter(
            (rect) => !sameCardRect(rect, fromRect) && !sameCardRect(rect, toRect),
        );
        maxHits = Math.max(maxHits, countNodeHits(poly.points, foreign));
    }
    return maxHits;
}

/** Count overlapping entity-card pairs (0 = no card–card collision). */
export function measureCardCardOverlaps(editor) {
    const cards = (editor.getCurrentPageShapes?.() || [])
        .filter((shape) => shape?.type === ENTITY_CARD_SHAPE_TYPE);
    let overlaps = 0;
    for (let i = 0; i < cards.length; i += 1) {
        const rectA = shapeRect(editor, cards[i].id);
        if (!rectA) continue;
        for (let j = i + 1; j < cards.length; j += 1) {
            const rectB = shapeRect(editor, cards[j].id);
            if (!rectB) continue;
            if (rectsOverlap(rectA, rectB)) {
                overlaps += 1;
            }
        }
    }
    return overlaps;
}

/**
 * Iteratively nudge entity cards apart until no pairwise overlaps remain.
 * @param {import('tldraw').Editor} editor
 * @param {number} [maxPasses]
 */
export function resolveEntityCardOverlaps(editor, maxPassesOrOptions = 24) {
    if (!editor) return;
    let maxPasses = 24;
    let pad = COLLISION_PAD;
    if (typeof maxPassesOrOptions === 'object' && maxPassesOrOptions) {
        maxPasses = Number.isFinite(maxPassesOrOptions.maxPasses)
            ? maxPassesOrOptions.maxPasses
            : 24;
        pad = Number.isFinite(maxPassesOrOptions.pad)
            ? maxPassesOrOptions.pad
            : COLLISION_PAD;
    } else if (Number.isFinite(maxPassesOrOptions)) {
        maxPasses = maxPassesOrOptions;
    }
    for (let pass = 0; pass < maxPasses; pass += 1) {
        const cards = (editor.getCurrentPageShapes?.() || [])
            .filter((shape) => shape?.type === ENTITY_CARD_SHAPE_TYPE);
        let moved = false;
        for (let i = 0; i < cards.length; i += 1) {
            const rectA = shapeRect(editor, cards[i].id);
            if (!rectA) continue;
            for (let j = i + 1; j < cards.length; j += 1) {
                const shapeB = cards[j];
                const rectB = shapeRect(editor, shapeB.id);
                if (!rectB || !rectsOverlap(rectA, rectB, pad)) continue;
                const delta = minTranslationToClear(rectB, rectA, pad);
                editor.updateShapes?.([{
                    id: shapeB.id,
                    type: ENTITY_CARD_SHAPE_TYPE,
                    x: shapeB.x + delta.x,
                    y: shapeB.y + delta.y,
                }]);
                moved = true;
            }
        }
        if (!moved) break;
    }
}

/**
 * Existing arrows that must be recomputed after a new card or a light nudge.
 * Hits on the new card, plus every edge bound to a card we just moved.
 *
 * @param {import('tldraw').Editor} editor
 * @param {{
 *   newCardRects?: Array<{x: number, y: number, w: number, h: number}>,
 *   nudgedShapeIds?: Array<string>|Set<string>,
 *   excludeArrowIds?: Array<string>|Set<string>,
 * }} [options]
 * @returns {Set<string>}
 */
export function collectArrowsToReroute(editor, options = {}) {
    const ids = new Set();
    if (!editor) return ids;
    const newCardRects = options.newCardRects || [];
    const nudged = new Set(options.nudgedShapeIds || []);
    const exclude = new Set(options.excludeArrowIds || []);

    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        if (exclude.has(shape.id)) continue;
        const poly = arrowPolylineForShape(editor, shape);
        if (!poly) continue;
        if (nudged.has(poly.startId) || nudged.has(poly.endId)) {
            ids.add(shape.id);
            continue;
        }
        for (const rect of newCardRects) {
            if (countCardInteriorEdgeHits(rect, [{points: poly.points}]) > 0) {
                ids.add(shape.id);
                break;
            }
        }
    }
    return ids;
}

/**
 * Arrow ids that share a hub target with at least one other incoming edge.
 *
 * @param {import('tldraw').Editor} editor
 * @returns {Set<string>}
 */
export function collectFanInHubArrowIds(editor) {
    const ids = new Set();
    if (!editor) return ids;
    /** @type {Map<string, Array<string>>} */
    const byTarget = new Map();
    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        const bindings = getArrowBindings(editor, shape);
        const endId = bindings?.end?.toId;
        if (!endId) continue;
        if (!byTarget.has(endId)) byTarget.set(endId, []);
        byTarget.get(endId).push(shape.id);
    }
    for (const arrowIds of byTarget.values()) {
        if (arrowIds.length < 2) continue;
        for (const id of arrowIds) ids.add(id);
    }
    return ids;
}

const GRAVITY_RELATION_RE = /member|leader|subordinate|reports|affiliated/;

/**
 * Org-chart gravity: member / subordinate / leader land under the org even when
 * the Wikidata arrow points person → org.
 *
 * @param {string|null|undefined} label
 * @returns {boolean}
 */
export function isSemanticGravityRelation(label) {
    return GRAVITY_RELATION_RE.test(String(label || '').toLowerCase());
}

/**
 * Peek at planned arrows to resolve the visual parent for a card being added.
 * Gravity relations win over peers such as "rivaled with".
 *
 * @param {string} entityRef
 * @param {Array<object>} mutations
 * @param {Map<string, string>} entityShapeIds
 * @returns {{parentRef: string, role: 'child'|'parent'|'neighbor', searchFrom: 'below'|'above'}|null}
 */
export function resolveSemanticParentRef(entityRef, mutations, entityShapeIds) {
    const ref = normalizeEntityRef(entityRef);
    if (!ref) return null;

    let gravityChild = null;
    let neighbor = null;
    for (const other of mutations || []) {
        if (other?.op !== 'add_arrow') continue;
        const from = normalizeEntityRef(other.fromEntityRef);
        const to = normalizeEntityRef(other.toEntityRef);
        const gravity = isSemanticGravityRelation(other.label || other.relation);
        if (from === ref && to && entityShapeIds.has(to)) {
            if (gravity) {
                return {parentRef: to, role: 'child', searchFrom: 'below'};
            }
            if (!neighbor) {
                neighbor = {parentRef: to, role: 'neighbor', searchFrom: 'below'};
            }
        } else if (to === ref && from && entityShapeIds.has(from)) {
            if (gravity && !gravityChild) {
                gravityChild = {parentRef: from, role: 'parent', searchFrom: 'above'};
            }
            if (!neighbor) {
                neighbor = {parentRef: from, role: 'neighbor', searchFrom: 'below'};
            }
        }
    }
    return gravityChild || neighbor;
}

/**
 * Rects for on-chart entities this card will connect to in the current plan.
 * Arrow endpoints beat suggestedAnchor — placement should hug connection targets.
 *
 * @param {import('tldraw').Editor} editor
 * @param {object} mutation
 * @param {Array<object>} mutations
 * @param {Map<string, string>} entityShapeIds
 * @returns {Array<{x: number, y: number, w: number, h: number}>}
 */
export function findConnectionTargetRectsForNewCard(editor, mutation, mutations, entityShapeIds) {
    const entityRef = normalizeEntityRef(mutation?.entityRef);
    if (mutation?.anchorShapeId) {
        const anchorRect = shapeRect(editor, mutation.anchorShapeId);
        if (anchorRect) return [anchorRect];
    }

    const semantic = resolveSemanticParentRef(entityRef, mutations, entityShapeIds);
    if (semantic && entityShapeIds.has(semantic.parentRef)) {
        const parentRect = shapeRect(editor, entityShapeIds.get(semantic.parentRef));
        if (parentRect) return [parentRect];
    }

    const rects = [];
    const seen = new Set();

    for (const other of mutations || []) {
        if (other?.op !== 'add_arrow') continue;
        let neighborRef = null;
        let neighborShapeId = null;
        if (normalizeEntityRef(other.fromEntityRef) === entityRef) {
            neighborRef = normalizeEntityRef(other.toEntityRef);
            neighborShapeId = entityShapeIds.get(neighborRef);
        } else if (normalizeEntityRef(other.toEntityRef) === entityRef) {
            neighborRef = normalizeEntityRef(other.fromEntityRef);
            neighborShapeId = resolveEntityShapeId(
                editor,
                neighborRef,
                entityShapeIds,
                other.fromShapeId,
            );
        }
        if (!neighborRef || seen.has(neighborRef)) continue;
        if (!neighborShapeId && !entityShapeIds.has(neighborRef)) continue;
        const rect = shapeRect(editor, neighborShapeId || entityShapeIds.get(neighborRef));
        if (rect) {
            seen.add(neighborRef);
            rects.push(rect);
        }
    }

    if (rects.length > 0) return rects;

    const anchorMatch = typeof mutation?.suggestedAnchor === 'string'
        ? mutation.suggestedAnchor.match(/^near:([^:]+)(?::offset:\d+)?$/)
        : null;
    const anchorRef = anchorMatch?.[1] || null;
    if (anchorRef) {
        const anchorShapeId = resolveEntityShapeId(
            editor,
            anchorRef,
            entityShapeIds,
            mutation.anchorShapeId,
        );
        if (anchorShapeId) {
            const rect = shapeRect(editor, anchorShapeId);
            if (rect) return [rect];
        }
    }
    return [];
}

/**
 * @deprecated Use findConnectionTargetRectsForNewCard — returns first target only.
 */
export function findHubRectForNewCard(editor, mutation, mutations, entityShapeIds) {
    const targets = findConnectionTargetRectsForNewCard(editor, mutation, mutations, entityShapeIds);
    return targets[0] || null;
}

/**
 * Polylines for planned add_arrow ops whose endpoints are already on the chart
 * (including cards added earlier in this apply batch).
 *
 * @param {import('tldraw').Editor} editor
 * @param {Array<object>} mutations
 * @param {Map<string, string>} entityShapeIds
 * @returns {Array<{
 *   points: Array<{x: number, y: number}>,
 *   labelPoint: {x: number, y: number},
 *   labelBox: {x: number, y: number, w: number, h: number}|null,
 * }>}
 */
export function collectPendingEdgePolylines(editor, mutations, entityShapeIds) {
    const polylines = [];
    for (const mutation of mutations || []) {
        if (mutation?.op !== 'add_arrow') continue;
        const fromRef = normalizeEntityRef(mutation.fromEntityRef);
        const toRef = normalizeEntityRef(mutation.toEntityRef);
        const fromId = entityShapeIds.get(fromRef);
        const toId = entityShapeIds.get(toRef);
        if (!fromId || !toId) continue;
        if (hasArrowBetweenShapes(editor, fromId, toId)) continue;
        const fromRect = shapeRect(editor, fromId);
        const toRect = shapeRect(editor, toId);
        if (!fromRect || !toRect) continue;
        const label = mutation.label || mutation.relation || null;
        const route = pickArcArrowRoute({fromRect, toRect, label});
        const labelPoint = pointAlongPolyline(route.points, route.labelPosition ?? 0.5);
        polylines.push({
            points: route.points,
            labelPoint,
            labelBox: label ? collisionLabelBoxAt(labelPoint, placementLabelSize(label)) : null,
        });
    }
    return polylines;
}

/**
 * Collect axis-aligned bounds for existing entity cards (page space).
 * @param {import('tldraw').Editor} editor
 * @returns {Array<{x: number, y: number, w: number, h: number}>}
 */
export function collectEntityCardBounds(editor) {
    const bounds = [];
    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== ENTITY_CARD_SHAPE_TYPE) continue;
        const pageBounds = editor.getShapePageBounds?.(shape.id);
        if (pageBounds) {
            const width = pageBounds.w
                ?? (Number.isFinite(pageBounds.maxX) ? pageBounds.maxX - pageBounds.x : ENTITY_CARD_WIDTH);
            bounds.push({
                x: pageBounds.x,
                y: pageBounds.y,
                w: width || ENTITY_CARD_WIDTH,
                h: Math.max(pageBounds.h || 0, shape.props?.h || 0),
            });
            continue;
        }
        bounds.push({
            x: shape.x,
            y: shape.y,
            w: shape.props?.w || ENTITY_CARD_WIDTH,
            h: Math.max(shape.props?.h || 0, 80),
        });
    }
    return bounds;
}

function rectsOverlap(a, b, pad = COLLISION_PAD) {
    return !(
        a.x + a.w + pad <= b.x
        || b.x + b.w + pad <= a.x
        || a.y + a.h + pad <= b.y
        || b.y + b.h + pad <= a.y
    );
}

function cardRectAtCenter(centerX, centerY, height = PLACEMENT_CARD_HEIGHT) {
    return {
        x: centerX - ENTITY_CARD_WIDTH / 2,
        y: centerY - height / 2,
        w: ENTITY_CARD_WIDTH,
        h: height,
    };
}

/**
 * Find a free center point near a preferred base, avoiding overlaps with existing cards.
 * @param {{x: number, y: number}} preferredCenter
 * @param {Array<{x: number, y: number, w: number, h: number}>} occupied
 * @param {number} addIndex
 * @returns {{x: number, y: number}}
 */
export function findNonOverlappingCardCenter(preferredCenter, occupied, addIndex = 0) {
    const stepX = ENTITY_CARD_WIDTH + GRID_GAP;
    const stepY = PLACEMENT_CARD_HEIGHT + GRID_GAP;
    const spiral = [];
    for (let ring = 0; ring <= 6; ring += 1) {
        if (ring === 0) {
            spiral.push([0, 0]);
            continue;
        }
        for (let dx = -ring; dx <= ring; dx += 1) {
            for (let dy = -ring; dy <= ring; dy += 1) {
                if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
                spiral.push([dx, dy]);
            }
        }
    }

    const startCol = addIndex % GRID_COLS;
    const startRow = Math.floor(addIndex / GRID_COLS);

    for (let i = 0; i < Math.min(MAX_PLACEMENT_ATTEMPTS, spiral.length); i += 1) {
        const [dx, dy] = spiral[i];
        const center = {
            x: preferredCenter.x + (startCol + dx) * stepX,
            y: preferredCenter.y + (startRow + dy) * stepY,
        };
        const candidate = cardRectAtCenter(center.x, center.y);
        if (!occupied.some((rect) => rectsOverlap(candidate, rect))) {
            return center;
        }
    }

    // Fallback: push far right of the densest cluster
    return {
        x: preferredCenter.x + (GRID_COLS + addIndex + 1) * stepX,
        y: preferredCenter.y,
    };
}

/**
 * Resolve page-point placement for a new entity card.
 * `suggestedAnchor` of the form `near:Q878` places near that card's bounds;
 * bulk adds search around the hub so a short unobstructed elbow is possible.
 *
 * @param {import('tldraw').Editor} editor
 * @param {{suggestedAnchor?: string}} mutation
 * @param {number} addIndex — 0-based index among add_entity_card ops in this apply
 * @param {Map<string, string>} entityShapeIds
 * @param {Array<{x: number, y: number, w: number, h: number}>} [extraOccupied]
 * @param {{existingEdges?: Array<object>, hubRect?: object}} [routingOptions]
 * @returns {{x: number, y: number}} page point (card center)
 */
export function resolveEntityCardPagePoint(
    editor,
    mutation,
    addIndex,
    entityShapeIds,
    extraOccupied = [],
    routingOptions = {}
) {
    const occupied = [...collectEntityCardBounds(editor), ...extraOccupied];
    const pendingEdges = routingOptions.pendingEdges || [];
    const existingEdges = [
        ...(routingOptions.existingEdges || collectExistingEdgePolylines(editor)),
        ...pendingEdges,
    ];

    const connectionTargets = routingOptions.connectionTargets?.length
        ? routingOptions.connectionTargets
        : (routingOptions.hubRect ? [routingOptions.hubRect] : []);

    if (connectionTargets.length > 0) {
        const semantic = resolveSemanticParentRef(
            mutation?.entityRef,
            routingOptions.mutations || [],
            entityShapeIds
        );
        const anchorMatch = typeof mutation?.suggestedAnchor === 'string'
            ? mutation.suggestedAnchor.match(/^near:([^:]+)(?::offset:(\d+))?$/)
            : null;
        const anchorOffset = anchorMatch?.[2] != null ? Number(anchorMatch[2]) : null;
        const offsetIndex = Number.isFinite(anchorOffset) && anchorOffset > 0
            ? addIndex + anchorOffset
            : addIndex;
        const edgeLabel = findPlannedEdgeLabelForCard(
            mutation,
            routingOptions.mutations || [],
        );
        const minEdgeLength = edgeLabel
            ? estimateMinEdgeLengthForLabel(edgeLabel)
            : 0;
        return pickBestCardCenter({
            hubRect: connectionTargets[0],
            targetRects: connectionTargets,
            occupied,
            cardW: ENTITY_CARD_WIDTH,
            cardH: PLACEMENT_CARD_HEIGHT,
            gap: GRID_GAP,
            collisionPad: COLLISION_PAD,
            existingEdges,
            addIndex: offsetIndex,
            searchFrom: semantic?.searchFrom || routingOptions.searchFrom || 'below',
            minEdgeLength,
        });
    }

    let baseX = 200;
    let baseY = 200;

    const anchorMatch = typeof mutation?.suggestedAnchor === 'string'
        ? mutation.suggestedAnchor.match(/^near:([^:]+)(?::offset:(\d+))?$/)
        : null;
    const anchorRef = anchorMatch?.[1] || null;
    const anchorOffset = anchorMatch?.[2] != null ? Number(anchorMatch[2]) : null;
    const anchorShapeId = anchorRef ? entityShapeIds.get(anchorRef) : null;

    if (anchorShapeId) {
        const bounds = editor.getShapePageBounds?.(anchorShapeId);
        const shape = editor.getShape?.(anchorShapeId);
        if (bounds) {
            baseX = bounds.maxX + GRID_GAP + ENTITY_CARD_WIDTH / 2;
            baseY = bounds.y + (bounds.h || PLACEMENT_CARD_HEIGHT) / 2;
        } else if (shape) {
            baseX = shape.x + ENTITY_CARD_WIDTH + GRID_GAP + ENTITY_CARD_WIDTH / 2;
            baseY = shape.y + PLACEMENT_CARD_HEIGHT / 2;
        }
        if (Number.isFinite(anchorOffset) && anchorOffset > 0) {
            baseY += anchorOffset * (PLACEMENT_CARD_HEIGHT + GRID_GAP);
        }
    } else {
        const container = editor.getContainer?.();
        if (container && typeof editor.screenToPage === 'function') {
            const rect = container.getBoundingClientRect();
            const center = editor.screenToPage({
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2,
            });
            baseX = center.x;
            baseY = center.y;
        }
    }

    return findNonOverlappingCardCenter({x: baseX, y: baseY}, occupied, addIndex);
}

/**
 * Shortest axis-aligned translation that separates `moving` from `blocker`.
 *
 * @param {{x: number, y: number, w: number, h: number}} moving
 * @param {{x: number, y: number, w: number, h: number}} blocker
 * @param {number} pad
 * @returns {{x: number, y: number}}
 */
export function minTranslationToClear(moving, blocker, pad = COLLISION_PAD) {
    const options = [
        {x: (blocker.x + blocker.w + pad) - moving.x, y: 0},
        {x: (blocker.x - moving.w - pad) - moving.x, y: 0},
        {x: 0, y: (blocker.y + blocker.h + pad) - moving.y},
        {x: 0, y: (blocker.y - moving.h - pad) - moving.y},
    ];
    let best = options[0];
    let bestLen = Math.hypot(best.x, best.y);
    for (let i = 1; i < options.length; i += 1) {
        const len = Math.hypot(options[i].x, options[i].y);
        if (len < bestLen) {
            best = options[i];
            bestLen = len;
        }
    }
    return best;
}

/**
 * Shove only cards that overlap the new card (or its new curve). Minimum axis shove.
 *
 * @param {import('tldraw').Editor} editor
 * @param {{x: number, y: number, w: number, h: number}} newCardRect
 * @param {{excludeShapeIds?: Array<string>|Set<string>, curvePoints?: Array<{x: number, y: number}>}} [options]
 * @returns {string[]} nudged shape ids
 */
export function nudgeOverlappingCards(editor, newCardRect, options = {}) {
    if (!editor || (!newCardRect && !options.curvePoints?.length)) return [];
    const exclude = new Set(options.excludeShapeIds || []);
    const curvePoints = options.curvePoints || null;
    const nudged = [];

    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== ENTITY_CARD_SHAPE_TYPE) continue;
        if (exclude.has(shape.id)) continue;
        const rect = shapeRect(editor, shape.id);
        if (!rect) continue;
        const overlapsCard = Boolean(newCardRect?.w || newCardRect?.h)
            && rectsOverlap(rect, newCardRect, COLLISION_PAD);
        const overlapsCurve = Boolean(curvePoints?.length) && countNodeHits(curvePoints, [rect]) > 0;
        if (!overlapsCard && !overlapsCurve) continue;

        const blocker = overlapsCard
            ? newCardRect
            : boundsOfPoints(curvePoints);
        const delta = minTranslationToClear(rect, blocker, COLLISION_PAD);
        const nextX = shape.x + delta.x;
        const nextY = shape.y + delta.y;
        if (Math.abs(nextX - shape.x) < 1 && Math.abs(nextY - shape.y) < 1) continue;
        editor.updateShapes?.([{
            id: shape.id,
            type: ENTITY_CARD_SHAPE_TYPE,
            x: nextX,
            y: nextY,
        }]);
        nudged.push(shape.id);
    }
    return nudged;
}

function boundsOfPoints(points) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const point of points || []) {
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
    }
    return {
        x: minX,
        y: minY,
        w: Math.max(0, maxX - minX),
        h: Math.max(0, maxY - minY),
    };
}

/**
 * If a newly created card still overlaps others, nudge it to a free slot.
 * @param {import('tldraw').Editor} editor
 * @param {string} shapeId
 */
export function nudgeEntityCardIfColliding(editor, shapeId) {
    const shape = editor.getShape?.(shapeId);
    if (!shape || shape.type !== ENTITY_CARD_SHAPE_TYPE) return;

    const pageBounds = editor.getShapePageBounds?.(shapeId);
    const self = pageBounds
        ? {
            x: pageBounds.x,
            y: pageBounds.y,
            w: pageBounds.w || ENTITY_CARD_WIDTH,
            h: Math.max(pageBounds.h || 0, shape.props?.h || 0, PLACEMENT_CARD_HEIGHT),
        }
        : {
            x: shape.x,
            y: shape.y,
            w: shape.props?.w || ENTITY_CARD_WIDTH,
            h: Math.max(shape.props?.h || 0, PLACEMENT_CARD_HEIGHT),
        };

    const others = collectEntityCardBounds(editor).filter((rect) => !(
        Math.abs(rect.x - self.x) < 1 && Math.abs(rect.y - self.y) < 1
    ));
    if (!others.some((rect) => rectsOverlap(self, rect))) return;

    const preferred = {
        x: self.x + self.w / 2,
        y: self.y + self.h / 2,
    };
    let hubRect = others[0];
    let bestDist = Infinity;
    for (const rect of others) {
        const dx = rect.x + rect.w / 2 - preferred.x;
        const dy = rect.y + rect.h / 2 - preferred.y;
        const dist = Math.hypot(dx, dy);
        if (dist < bestDist) {
            bestDist = dist;
            hubRect = rect;
        }
    }
    const freeCenter = hubRect
        ? pickBestCardCenter({
            hubRect,
            occupied: others,
            cardW: self.w || ENTITY_CARD_WIDTH,
            cardH: self.h || PLACEMENT_CARD_HEIGHT,
            gap: GRID_GAP,
            collisionPad: COLLISION_PAD,
            existingEdges: collectExistingEdgePolylines(editor),
        })
        : findNonOverlappingCardCenter(preferred, others, 0);
    const nextX = freeCenter.x - self.w / 2;
    const nextY = freeCenter.y - self.h / 2;
    if (Math.abs(nextX - shape.x) < 1 && Math.abs(nextY - shape.y) < 1) return;
    editor.updateShapes?.([{
        id: shapeId,
        type: ENTITY_CARD_SHAPE_TYPE,
        x: nextX,
        y: nextY,
    }]);
}

/**
 * @param {import('tldraw').Editor} editor
 * @param {string} fromShapeId
 * @param {string} toShapeId
 * @returns {string|null} arrow shape id if bound from→to
 */
export function findArrowBetweenShapes(editor, fromShapeId, toShapeId) {
    const shapes = editor.getCurrentPageShapes?.() || [];
    for (const shape of shapes) {
        if (shape?.type !== 'arrow') continue;
        const bindings = getArrowBindings(editor, shape);
        if (bindings?.start?.toId === fromShapeId && bindings?.end?.toId === toShapeId) {
            return shape.id;
        }
    }
    return null;
}

/**
 * @param {import('tldraw').Editor} editor
 * @param {string} fromShapeId
 * @param {string} toShapeId
 * @returns {boolean}
 */
export function hasArrowBetweenShapes(editor, fromShapeId, toShapeId) {
    return Boolean(findArrowBetweenShapes(editor, fromShapeId, toShapeId));
}

/**
 * Plain text → TLDraw richText doc (avoids depending on a specific toRichText export path).
 * @param {string} text
 * @returns {{type: string, content: Array<object>}}
 */
export function arrowLabelRichText(text) {
    const value = typeof text === 'string' ? text : '';
    if (!value) {
        return {type: 'doc', content: [{type: 'paragraph'}]};
    }
    return {
        type: 'doc',
        content: [{
            type: 'paragraph',
            content: [{type: 'text', text: value}],
        }],
    };
}

/**
 * @param {object|null|undefined} shape
 * @returns {string}
 */
export function arrowLabelTextFromShape(shape) {
    const content = shape?.props?.richText?.content;
    if (!Array.isArray(content)) return '';
    for (const para of content) {
        for (const node of para?.content || []) {
            if (node?.type === 'text' && node.text) {
                return String(node.text).trim();
            }
        }
    }
    return '';
}

/**
 * Match color/size of existing chart arrows. Defaults to elbow for from-scratch builds;
 * incremental adds override to arc in {@link createArrowBetweenShapes}.
 *
 * @param {import('tldraw').Editor} editor
 * @param {string|null|undefined} label
 * @returns {Record<string, unknown>}
 */
export function resolveChartArrowProps(editor, label) {
    const existing = (editor.getCurrentPageShapes?.() || []).find((s) => s?.type === 'arrow');
    const sample = existing?.props || {};
    const safeLabel = sanitizeArrowRelationLabel(label);
    const props = {
        kind: 'elbow',
        size: sample.size || 's',
        color: sample.color || 'black',
        dash: sample.dash || 'solid',
        fill: sample.fill || 'none',
        font: sample.font || 'draw',
        arrowheadStart: sample.arrowheadStart || 'none',
        arrowheadEnd: sample.arrowheadEnd || 'arrow',
        labelColor: sample.labelColor || 'black',
        bend: 0,
        labelPosition: 0.5,
        scale: 1,
        elbowMidPoint: 0.5,
        start: {x: 0, y: 0},
        end: {x: 0, y: 0},
        richText: arrowLabelRichText(safeLabel || ''),
    };
    return props;
}

/**
 * Count arrows already bound into a target card (for fan-in arc staggering).
 * @param {import('tldraw').Editor} editor
 * @param {string} toShapeId
 * @returns {number}
 */
function countArrowsToTarget(editor, toShapeId) {
    let count = 0;
    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        const bindings = getArrowBindings(editor, shape);
        if (bindings?.end?.toId === toShapeId) count += 1;
    }
    return count;
}

/** @typedef {'straight'|'curved'|'elbow'} ChartEdgeStyle */

/**
 * Classify a TLDraw arrow as a chart edge style.
 * Elbows use `kind: 'elbow'`; arcs with no bend are straight; bent arcs are curved.
 *
 * @param {object|null|undefined} shape
 * @returns {ChartEdgeStyle|null}
 */
export function chartEdgeStyleFromArrow(shape) {
    if (!shape || shape.type !== 'arrow') return null;
    if (shape.props?.kind === 'elbow') return 'elbow';
    const bend = Number(shape.props?.bend) || 0;
    return Math.abs(bend) > 0.01 ? 'curved' : 'straight';
}

/**
 * Most common bound-or-unbound arrow style on the current page.
 * Ties break in the order straight → curved → elbow. Empty charts default to
 * `straight` so the first Pure LLM edge matches the existing apply path.
 *
 * @param {import('tldraw').Editor} editor
 * @param {ChartEdgeStyle} [fallback='straight']
 * @returns {ChartEdgeStyle}
 */
export function mostCommonChartEdgeStyle(editor, fallback = 'straight') {
    const counts = {straight: 0, curved: 0, elbow: 0};
    for (const shape of editor?.getCurrentPageShapes?.() || []) {
        const style = chartEdgeStyleFromArrow(shape);
        if (style) counts[style] += 1;
    }
    let best = fallback;
    let bestCount = 0;
    for (const style of ['straight', 'curved', 'elbow']) {
        if (counts[style] > bestCount) {
            best = style;
            bestCount = counts[style];
        }
    }
    return best;
}

/**
 * Create a TLDraw arrow bound between two entity-cards.
 * Pass `style` to force straight / curved / elbow; otherwise preferStraight
 * or useArc select the route (default is elbow).
 *
 * @param {import('tldraw').Editor} editor
 * @param {string} fromShapeId
 * @param {string} toShapeId
 * @param {{label?: string|null, style?: ChartEdgeStyle, useArc?: boolean, preferStraight?: boolean, arcIndex?: number, simple?: boolean}} [options]
 * @returns {string} arrow shape id
 */
export function createArrowBetweenShapes(editor, fromShapeId, toShapeId, options = {}) {
    const fromShape = editor.getShape?.(fromShapeId);
    const toShape = editor.getShape?.(toShapeId);
    if (!fromShape || !toShape) {
        throw new Error('Cannot create arrow: missing endpoint shape');
    }

    const fromRect = shapeRect(editor, fromShapeId);
    const toRect = shapeRect(editor, toShapeId);
    const obstacles = options.obstacles || collectEntityCardBounds(editor);
    const existingEdges = options.existingEdges || collectExistingEdgePolylines(editor);
    const style = options.style;
    const preferStraight = (style === 'straight' || (style == null && options.preferStraight === true))
        && !options.simple && fromRect && toRect;
    const useArc = !preferStraight
        && (style === 'curved' || (style == null && options.useArc === true))
        && !options.simple && fromRect && toRect;
    const props = resolveChartArrowProps(editor, options.label);
    let routeKind = null;
    let route = null;
    if (preferStraight) {
        const pick = pickStraightOrArcArrowRoute({
            fromRect,
            toRect,
            obstacles,
            existingEdges,
            arcIndex: options.arcIndex ?? 0,
            label: options.label,
        });
        routeKind = pick.kind;
        route = pick.route;
    } else if (useArc) {
        routeKind = 'arc';
        route = pickArcArrowRoute({
            fromRect,
            toRect,
            obstacles,
            existingEdges,
            arcIndex: options.arcIndex ?? 0,
            allowWideDetour: options.allowWideDetour === true,
            label: options.label,
        });
    } else if (!options.simple && fromRect && toRect) {
        routeKind = 'elbow';
        route = pickBestArrowRoute({
            fromRect,
            toRect,
            obstacles,
            existingEdges,
            label: options.label,
            strokeWidth: arrowStrokeWidth(props.size),
        });
    }

    const arrowId = createShapeId();
    if (route) {
        if (routeKind === 'arc' || useArc || preferStraight) {
            props.kind = 'arc';
            props.bend = route.bend;
            props.labelPosition = route.labelPosition ?? 0.5;
        } else {
            props.kind = 'elbow';
            props.bend = 0;
            props.elbowMidPoint = route.elbowMidPoint;
            props.labelPosition = route.labelPosition;
        }
    }
    editor.createShapes([{
        id: arrowId,
        type: 'arrow',
        x: fromShape.x,
        y: fromShape.y,
        props,
        ...(options.authorId ? {meta: mergeAuthorshipMeta(null, options.authorId)} : {}),
    }]);
    const startAnchor = route
        ? normalizedAnchorForSide(route.fromSide, route.fromT)
        : {x: 0.5, y: options.simple ? 1 : 0.5};
    const endAnchor = route
        ? normalizedAnchorForSide(route.toSide, route.toT)
        : {x: 0.5, y: options.simple ? 0 : 0.5};
    editor.createBindings([
        {
            id: createBindingId(),
            type: 'arrow',
            fromId: arrowId,
            toId: fromShapeId,
            props: {
                terminal: 'start',
                isExact: false,
                isPrecise: Boolean(route),
                snap: 'edge',
                normalizedAnchor: startAnchor,
            },
        },
        {
            id: createBindingId(),
            type: 'arrow',
            fromId: arrowId,
            toId: toShapeId,
            props: {
                terminal: 'end',
                isExact: false,
                isPrecise: Boolean(route),
                snap: 'edge',
                normalizedAnchor: endAnchor,
            },
        },
    ]);
    spreadChartArrowEntryPoints(editor);
    return arrowId;
}

/**
 * Sort key along a card side so attachments stay in source-order (Y on left/right, X on top/bottom).
 *
 * @param {'right'|'bottom'|'left'|'top'} side
 * @param {{x: number, y: number, w: number, h: number}|null} otherRect
 * @returns {number}
 */
function sideAttachmentSortKey(side, otherRect) {
    if (!otherRect) return 0;
    const other = rectCenter(otherRect);
    return (side === 'left' || side === 'right') ? other.y : other.x;
}

/**
 * Side an arrow binding intends to use: existing anchor, else facing/enter from the other card.
 *
 * @param {'start'|'end'} terminal
 * @param {{x: number, y: number}|null|undefined} anchor
 * @param {{x: number, y: number, w: number, h: number}|null} cardRect
 * @param {{x: number, y: number, w: number, h: number}|null} otherRect
 * @returns {'right'|'bottom'|'left'|'top'|null}
 */
function resolveAttachmentSide(terminal, anchor, cardRect, otherRect) {
    const fromAnchor = sideFromNormalizedAnchor(anchor);
    if (fromAnchor) return fromAnchor;
    if (!cardRect || !otherRect) return null;
    return terminal === 'start'
        ? facingSide(cardRect, otherRect)
        : preferredEnterSide(cardRect, otherRect);
}

/**
 * Spread `normalizedAnchor` evenly along each card side so arrows never share a point.
 * Keeps each binding on its intended side; only n>=2 groups are rewritten.
 *
 * @param {import('tldraw').Editor} editor
 */
export function spreadChartArrowEntryPoints(editor) {
    if (!isChartSpreadEntryPointsEnabled()) return;
    if (!editor) return;

    /** @type {Map<string, Array<{binding: object, side: string, sortKey: number}>>} */
    const groups = new Map();

    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        const bindings = getArrowBindings(editor, shape);
        const startId = bindings?.start?.toId;
        const endId = bindings?.end?.toId;
        if (!startId || !endId) continue;
        const fromRect = shapeRect(editor, startId);
        const toRect = shapeRect(editor, endId);

        const add = (binding, terminal, cardRect, otherRect) => {
            if (!binding) return;
            const side = resolveAttachmentSide(
                terminal,
                binding.props?.normalizedAnchor,
                cardRect,
                otherRect,
            );
            if (!side) return;
            const key = `${binding.toId}\0${side}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push({
                binding,
                side,
                sortKey: sideAttachmentSortKey(side, otherRect),
            });
        };

        add(bindings.start, 'start', fromRect, toRect);
        add(bindings.end, 'end', toRect, fromRect);
    }

    const bindingUpdates = [];
    for (const attachments of groups.values()) {
        if (attachments.length < 2) continue;
        attachments.sort((a, b) => {
            const byPos = a.sortKey - b.sortKey;
            if (byPos !== 0) return byPos;
            return String(a.binding.id || '').localeCompare(String(b.binding.id || ''));
        });
        const anchors = spreadNormalizedAnchorsForSide(attachments[0].side, attachments.length);
        for (let i = 0; i < attachments.length; i += 1) {
            const {binding} = attachments[i];
            if (!binding.id) continue;
            bindingUpdates.push({
                id: binding.id,
                type: 'arrow',
                props: {
                    ...binding.props,
                    isPrecise: true,
                    normalizedAnchor: anchors[i],
                },
            });
        }
    }

    if (bindingUpdates.length && typeof editor.updateBindings === 'function') {
        editor.updateBindings(bindingUpdates);
    }
}

const FAN_IN_STAGGER_TS = [0.28, 0.5, 0.72];

/**
 * When several edges share the same label into one target, stagger label placement
 * along each spoke so "member of" / "affiliated with" stay readable without stacking
 * at the same point — labels are kept on every edge.
 *
 * @param {import('tldraw').Editor} editor
 */
export function dedupeFanInArrowLabels(editor) {
    if (!editor) return;

    /** @type {Map<string, Array<{shapeId: string, fromX: number}>>} */
    const groups = new Map();

    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        const label = arrowLabelTextFromShape(shape);
        if (!label) continue;
        const bindings = getArrowBindings(editor, shape);
        const endId = bindings?.end?.toId;
        const startId = bindings?.start?.toId;
        if (!endId || !startId) continue;
        const fromRect = shapeRect(editor, startId);
        const key = `${endId}\0${label.toLowerCase()}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({
            shapeId: shape.id,
            fromX: fromRect?.x ?? 0,
        });
    }

    const shapeUpdates = [];
    for (const entries of groups.values()) {
        if (entries.length <= 1) continue;
        entries.sort((a, b) => a.fromX - b.fromX);
        const count = entries.length;
        for (let i = 0; i < count; i += 1) {
            const shape = editor.getShape?.(entries[i].shapeId);
            if (!shape) continue;
            const labelPosition = count === 1
                ? 0.5
                : 0.25 + (i / (count - 1)) * 0.5;
            shapeUpdates.push({
                id: shape.id,
                type: 'arrow',
                props: {
                    ...shape.props,
                    labelPosition,
                },
            });
        }
    }

    if (shapeUpdates.length) {
        editor.updateShapes?.(shapeUpdates);
    }
}

/**
 * Recompute elbow routing and label placement for chart arrows.
 * When `arrowShapeIds` is set, only those arrows are rerouted (existing routes stay put).
 * When omitted, every bound arrow is rerouted — use after a full relayout or from-scratch build.
 *
 * @param {import('tldraw').Editor} editor
 * @param {Set<string>|Array<string>|null|undefined} [arrowShapeIds]
 */
export function rerouteAllChartArrows(editor, arrowShapeIds = null, options = {}) {
    if (!editor) return;

    const useArc = options.useArc !== false;
    const arcOnly = options.arcOnly === true;
    const preferStraight = options.preferStraight === true;
    const onlyIds = arrowShapeIds == null
        ? null
        : new Set(Array.isArray(arrowShapeIds) ? arrowShapeIds : [...arrowShapeIds]);
    const obstacles = collectEntityCardBounds(editor);
    const allArrows = (editor.getCurrentPageShapes?.() || []).filter((s) => s?.type === 'arrow');
    const arrows = onlyIds
        ? allArrows.filter((s) => onlyIds.has(s.id))
        : allArrows;
    const pending = [];
    const fanInTo = new Map();

    for (const shape of arrows) {
        const bindings = getArrowBindings(editor, shape);
        const startId = bindings?.start?.toId;
        const endId = bindings?.end?.toId;
        if (!startId || !endId) continue;
        const fromRect = shapeRect(editor, startId);
        const toRect = shapeRect(editor, endId);
        if (!fromRect || !toRect) continue;
        pending.push({shape, bindings, endId, fromRect, toRect, label: arrowLabelTextFromShape(shape)});
        fanInTo.set(endId, (fanInTo.get(endId) || 0) + 1);
    }

    pending.sort((a, b) => {
        const toCmp = a.endId.localeCompare(b.endId);
        if (toCmp !== 0) return toCmp;
        return fanInSourceSortKey(a.fromRect, a.toRect)
            - fanInSourceSortKey(b.fromRect, b.toRect);
    });

    const existingEdges = onlyIds
        ? collectExistingEdgePolylines(editor, onlyIds)
        : [];
    const toAttachmentIndex = new Map();
    const shapeUpdates = [];
    const bindingUpdates = [];

    for (const item of pending) {
        const toIndex = toAttachmentIndex.get(item.endId) || 0;
        toAttachmentIndex.set(item.endId, toIndex + 1);
        const stagger = FAN_IN_STAGGER_TS[toIndex % FAN_IN_STAGGER_TS.length];
        const hasFanIn = (fanInTo.get(item.endId) || 0) > 1;

        const routePick = pickRoutedArrowRoute({
            fromRect: item.fromRect,
            toRect: item.toRect,
            obstacles,
            existingEdges,
            useArc,
            arcOnly,
            preferStraight,
            arcIndex: toIndex,
            preferredFromT: hasFanIn ? stagger : undefined,
            preferredToT: hasFanIn ? stagger : undefined,
            preferredBendSign: hasFanIn
                ? preferredFanInBendSign(item.fromRect, item.toRect)
                : null,
            label: item.label,
            strokeWidth: arrowStrokeWidth(item.shape.props?.size),
        });
        const route = routePick.route;

        shapeUpdates.push({
            id: item.shape.id,
            type: 'arrow',
            props: routePick.kind === 'arc'
                ? {
                    ...item.shape.props,
                    kind: 'arc',
                    bend: route.bend,
                    // Was pinned to the midpoint; now reuses the same
                    // overlap-aware slide (pickLabelPosition) elbows already
                    // got, so curved edges stop stacking their labels dead
                    // center when several arcs pass close together.
                    labelPosition: route.labelPosition ?? 0.5,
                }
                : {
                    ...item.shape.props,
                    kind: 'elbow',
                    bend: 0,
                    elbowMidPoint: route.elbowMidPoint,
                    labelPosition: route.labelPosition,
                },
        });

        const startAnchor = normalizedAnchorForSide(route.fromSide, route.fromT);
        const endAnchor = normalizedAnchorForSide(route.toSide, route.toT);
        if (item.bindings?.start?.id) {
            bindingUpdates.push({
                id: item.bindings.start.id,
                type: 'arrow',
                props: {
                    ...item.bindings.start.props,
                    isPrecise: true,
                    normalizedAnchor: startAnchor,
                },
            });
        }
        if (item.bindings?.end?.id) {
            bindingUpdates.push({
                id: item.bindings.end.id,
                type: 'arrow',
                props: {
                    ...item.bindings.end.props,
                    isPrecise: true,
                    normalizedAnchor: endAnchor,
                },
            });
        }

        const labelPoint = pointAlongPolyline(route.points, route.labelPosition);
        existingEdges.push({
            points: route.points,
            labelPoint,
            labelBox: item.label ? collisionLabelBoxAt(labelPoint, placementLabelSize(item.label)) : null,
            strokeWidth: arrowStrokeWidth(item.shape.props?.size),
        });
    }

    if (shapeUpdates.length) {
        editor.updateShapes?.(shapeUpdates);
    }
    if (bindingUpdates.length && typeof editor.updateBindings === 'function') {
        editor.updateBindings(bindingUpdates);
    }
    spreadChartArrowEntryPoints(editor);
}

/**
 * Force every bound chart arrow to a straight segment (arc with bend=0),
 * searching all 4 sides on each end for the clearest attachment rather than
 * a single fixed geometric guess — the "reconnect from any of the 4 sides"
 * behaviour used by the Straighten command and any layout whose edge style
 * resolves to 'straight' (Network, Radial).
 *
 * @param {import('tldraw').Editor} editor
 */
export function straightenAllChartArrows(editor) {
    if (!editor) return;

    const obstacles = collectEntityCardBounds(editor);
    const allArrows = (editor.getCurrentPageShapes?.() || []).filter((s) => s?.type === 'arrow');
    const pending = [];
    const fanInTo = new Map();

    for (const shape of allArrows) {
        const bindings = getArrowBindings(editor, shape);
        const startId = bindings?.start?.toId;
        const endId = bindings?.end?.toId;
        if (!startId || !endId) continue;
        const fromRect = shapeRect(editor, startId);
        const toRect = shapeRect(editor, endId);
        if (!fromRect || !toRect) continue;
        pending.push({shape, bindings, endId, fromRect, toRect, label: arrowLabelTextFromShape(shape)});
        fanInTo.set(endId, (fanInTo.get(endId) || 0) + 1);
    }

    // Same deterministic ordering as rerouteAllChartArrows so repeated runs
    // stagger fan-in the same way instead of depending on shape creation order.
    pending.sort((a, b) => {
        const toCmp = a.endId.localeCompare(b.endId);
        if (toCmp !== 0) return toCmp;
        return fanInSourceSortKey(a.fromRect, a.toRect)
            - fanInSourceSortKey(b.fromRect, b.toRect);
    });

    const toAttachmentIndex = new Map();
    // Accumulated so later straight lines in this pass avoid crossing earlier
    // ones and each other's labels, not just the static card obstacles.
    const existingEdges = [];
    const shapeUpdates = [];
    const bindingUpdates = [];

    for (const item of pending) {
        const toIndex = toAttachmentIndex.get(item.endId) || 0;
        toAttachmentIndex.set(item.endId, toIndex + 1);
        const hasFanIn = (fanInTo.get(item.endId) || 0) > 1;
        const stagger = FAN_IN_STAGGER_TS[toIndex % FAN_IN_STAGGER_TS.length];

        const route = pickBestStraightRoute({
            fromRect: item.fromRect,
            toRect: item.toRect,
            obstacles,
            existingEdges,
            preferredFromT: hasFanIn ? stagger : undefined,
            preferredToT: hasFanIn ? stagger : undefined,
            label: item.label,
        });

        shapeUpdates.push({
            id: item.shape.id,
            type: 'arrow',
            props: {
                ...item.shape.props,
                kind: 'arc',
                bend: 0,
                labelPosition: route.labelPosition ?? 0.5,
            },
        });

        const startAnchor = normalizedAnchorForSide(route.fromSide, route.fromT);
        const endAnchor = normalizedAnchorForSide(route.toSide, route.toT);
        if (item.bindings?.start?.id) {
            bindingUpdates.push({
                id: item.bindings.start.id,
                type: 'arrow',
                props: {
                    ...item.bindings.start.props,
                    isPrecise: true,
                    normalizedAnchor: startAnchor,
                },
            });
        }
        if (item.bindings?.end?.id) {
            bindingUpdates.push({
                id: item.bindings.end.id,
                type: 'arrow',
                props: {
                    ...item.bindings.end.props,
                    isPrecise: true,
                    normalizedAnchor: endAnchor,
                },
            });
        }

        const labelPoint = pointAlongPolyline(route.points, route.labelPosition ?? 0.5);
        existingEdges.push({
            points: route.points,
            labelPoint,
            labelBox: item.label ? collisionLabelBoxAt(labelPoint, placementLabelSize(item.label)) : null,
        });
    }

    if (shapeUpdates.length) {
        editor.updateShapes?.(shapeUpdates);
    }
    if (bindingUpdates.length && typeof editor.updateBindings === 'function') {
        editor.updateBindings(bindingUpdates);
    }
    spreadChartArrowEntryPoints(editor);
}

/**
 * Apply a chart-wide edge style to every bound arrow.
 *
 * @param {import('tldraw').Editor} editor
 * @param {ChartEdgeStyle} style
 * @param {{resolveLabels?: boolean}} [options] When `resolveLabels` is false,
 *   skip the trailing {@link dedupeFanInArrowLabels} so Arrange can own
 *   final placement. Defaults to true (today's toolbar / tidy behavior).
 */
export function applyChartEdgeStyle(editor, style, options = {}) {
    if (!editor) return;
    switch (style) {
        case 'straight':
            straightenAllChartArrows(editor);
            break;
        case 'curved':
            rerouteAllChartArrows(editor, null, {useArc: true, arcOnly: true});
            break;
        case 'elbow':
            rerouteAllChartArrows(editor, null, {useArc: false, arcOnly: false});
            break;
        default:
            break;
    }
    if (options.resolveLabels !== false) {
        dedupeFanInArrowLabels(editor);
    }
}

/**
 * Apply a ChartMutationPlan to the open TLDraw editor (client-only; never PATCHes snapshot).
 *
 * @param {object} plan — ChartMutationPlan `{ schemaVersion, chartId, summary, mutations, warnings }`
 * @param {{
 *   editor: import('tldraw').Editor,
 *   createEntityCardAtPagePoint?: (entity: object, pagePoint: {x: number, y: number}) => Promise<void>|void,
 * }} options
 * @returns {Promise<{
 *   applied: number,
 *   skipped: number,
 *   failed: Array<{mutation: object, error: string}>,
 *   results: Array<object>,
 *   existingEntityCountBefore: number,
 *   addedEntityCount: number,
 *   addedArrowCount: number,
 *   hydration: Promise<unknown>|null,
 * }>}
 */
export async function applyChartMutations(plan, {editor, createEntityCardAtPagePoint} = {}) {
    if (!editor) {
        throw new Error('Chart editor is not available');
    }
    const mutations = Array.isArray(plan?.mutations) ? plan.mutations : [];
    const existingCardCount = collectChartEntitiesFromEditor(editor).length;
    const cardAdds = mutations.filter((m) => m?.op === 'add_entity_card');
    const arrowMutations = mutations.filter((m) => m?.op === 'add_arrow');
    const preserveLayout = shouldPreserveLayout(existingCardCount, cardAdds, arrowMutations);
    // Empty-chart multi-add uses a simple grid then full relayout; incremental multi-adds
    // keep collision-aware placement. Batch UI flags (no per-card select / no blocking fetch)
    // still apply whenever more than one card is added.
    const useGridPlacement = cardAdds.length > 1 && !preserveLayout;
    const batchUi = cardAdds.length > 1;
    const entityShapeIds = indexEntityShapesByRef(editor);
    const labelHints = collectPlanLabelHints(mutations);
    const results = [];
    let applied = 0;
    let skipped = 0;
    let addedEntityCount = 0;
    let addedArrowCount = 0;
    const failed = [];
    let addIndex = 0;
    /** @type {Array<{x: number, y: number, w: number, h: number}>} */
    const sessionOccupied = [];
    /** @type {string[]} arrow shape ids created in this apply batch */
    const newArrowShapeIds = [];
    /** @type {Array<{x: number, y: number, w: number, h: number}>} */
    const newCardRects = [];
    /** @type {Set<string>} */
    const nudgedShapeIds = new Set();

    const rememberCreatedCard = (shapeId) => {
        const rect = shapeRect(editor, shapeId);
        if (!rect) return;
        const reserved = {
            ...rect,
            h: Math.max(rect.h || 0, PLACEMENT_CARD_HEIGHT),
        };
        sessionOccupied.push(reserved);
        newCardRects.push(reserved);
        for (const id of nudgeOverlappingCards(editor, reserved, {excludeShapeIds: [shapeId]})) {
            nudgedShapeIds.add(id);
        }
    };

    const addCard = async (entity, pagePoint) => {
        if (typeof createEntityCardAtPagePoint === 'function') {
            await createEntityCardAtPagePoint(entity, pagePoint, {
                batchChartApply: batchUi,
                selectShape: plan?.preserveEditorSelection ? false : !batchUi,
                // Structural apply must not block on /api/entities or Wikidata; hydrate after.
                skipWikidataFetch: true,
            });
            const refreshed = indexEntityShapesByRef(editor);
            for (const [ref, shapeId] of refreshed) {
                entityShapeIds.set(ref, shapeId);
            }
            const createdId = entityShapeIds.get(entity.wikidataId || entity.id);
            if (createdId) {
                rememberCreatedCard(createdId);
            }
            return;
        }
        const shapePropsHeight = PLACEMENT_CARD_HEIGHT;
        const position = getCenteredEntityCardPosition(pagePoint, shapePropsHeight);
        const shape = createEntityCardShape({
            entity,
            x: position.x,
            y: position.y,
        });
        editor.createShapes([shape]);
        entityShapeIds.set(entity.wikidataId || entity.id, shape.id);
        rememberCreatedCard(shape.id);
    };

    const cardMutations = cardAdds;
    const otherMutations = mutations.filter((mutation) =>
        mutation?.op !== 'add_entity_card' && mutation?.op !== 'add_arrow');

    const applyCardMutation = async (mutation) => {
        const entityRef = normalizeEntityRef(mutation.entityRef);
        if (!entityRef) {
            throw new Error('add_entity_card requires entityRef');
        }
        if (entityShapeIds.has(entityRef)) {
            skipped += 1;
            results.push({ok: true, skipped: true, mutation});
            return;
        }
        const pagePoint = useGridPlacement
            ? {
                x: 120 + (addIndex % 4) * (ENTITY_CARD_WIDTH + GRID_GAP),
                y: 120 + Math.floor(addIndex / 4) * (PLACEMENT_CARD_HEIGHT + GRID_GAP),
            }
            : resolveEntityCardPagePoint(
                editor,
                mutation,
                addIndex,
                entityShapeIds,
                sessionOccupied,
                {
                    mutations,
                    connectionTargets: findConnectionTargetRectsForNewCard(
                        editor, mutation, mutations, entityShapeIds
                    ),
                    pendingEdges: collectPendingEdgePolylines(editor, mutations, entityShapeIds),
                }
            );
        addIndex += 1;
        const label = resolveMutationEntityLabel({
            id: entityRef,
            wikidataId: entityRef,
            label: mutation.label,
            canonicalName: labelHints.get(entityRef),
        });
        await addCard({
            id: entityRef,
            wikidataId: entityRef,
            label,
        }, pagePoint);
        if (!entityShapeIds.has(entityRef)) {
            const found = (editor.getCurrentPageShapes?.() || []).find(
                (s) => s?.type === ENTITY_CARD_SHAPE_TYPE && s?.props?.wikidataId === entityRef
            );
            if (found) {
                entityShapeIds.set(entityRef, found.id);
                rememberCreatedCard(found.id);
            }
        }
        applyMachineAuthorshipToShape(editor, entityShapeIds.get(entityRef));
        addedEntityCount += 1;
        applied += 1;
        results.push({ok: true, mutation});
    };

    const applyArrowMutation = (mutation) => {
        const fromRef = normalizeEntityRef(mutation.fromEntityRef);
        const toRef = normalizeEntityRef(mutation.toEntityRef);
        if (!fromRef || !toRef) {
            throw new Error('add_arrow requires fromEntityRef and toEntityRef');
        }
        if (fromRef === toRef) {
            skipped += 1;
            results.push({ok: true, skipped: true, mutation, reason: 'self_referential'});
            return;
        }
        const fromShapeId = resolveEntityShapeId(editor, fromRef, entityShapeIds, mutation.fromShapeId);
        const toShapeId = resolveEntityShapeId(editor, toRef, entityShapeIds, mutation.toShapeId);
        if (!fromShapeId || !toShapeId) {
            throw new Error(`Cannot connect ${fromRef} → ${toRef}: entity card missing on chart`);
        }
        if (hasArrowBetweenShapes(editor, fromShapeId, toShapeId)) {
            skipped += 1;
            results.push({ok: true, skipped: true, mutation});
            return;
        }
        const arrowLabel = sanitizeArrowRelationLabel(
            mutation.label || mutation.relation || null
        );
        // Property drawer / Pure LLM path: always curved arcs.
        const arrowId = createArrowBetweenShapes(editor, fromShapeId, toShapeId, {
            label: arrowLabel,
            useArc: true,
            arcIndex: countArrowsToTarget(editor, toShapeId),
            allowWideDetour: mutation.allowWideDetour === true,
            authorId: AI_AUTHOR_UID,
        });
        newArrowShapeIds.push(arrowId);
        addedArrowCount += 1;
        applied += 1;
        results.push({ok: true, mutation});
    };

    for (const mutation of cardMutations) {
        try {
            await applyCardMutation(mutation);
        } catch (error) {
            const technical = error?.message || String(error);
            const message = formatChartErrorForUser(technical);
            failed.push({mutation, error: message});
            results.push({ok: false, mutation, error: message});
        }
    }

    for (const mutation of arrowMutations) {
        try {
            applyArrowMutation(mutation);
        } catch (error) {
            const technical = error?.message || String(error);
            const message = formatChartErrorForUser(technical);
            failed.push({mutation, error: message});
            results.push({ok: false, mutation, error: message});
        }
    }

    if (arrowMutations.length > 0 || newCardRects.length > 0 || nudgedShapeIds.size > 0) {
        for (const arrowId of newArrowShapeIds) {
            const shape = editor.getShape?.(arrowId);
            const poly = shape ? arrowPolylineForShape(editor, shape) : null;
            if (!poly?.points?.length) continue;
            for (const id of nudgeOverlappingCards(editor, null, {
                excludeShapeIds: [poly.startId, poly.endId],
                curvePoints: poly.points,
            })) {
                nudgedShapeIds.add(id);
            }
        }
        const rerouteIds = collectArrowsToReroute(editor, {
            newCardRects,
            nudgedShapeIds,
            excludeArrowIds: newArrowShapeIds,
        });
        // Property "Add to chart" / incremental AI adds create arcs — keep them
        // bent after nudge/fan-in (without arcOnly, pickRoutedArrowRoute can flip to elbows).
        if (rerouteIds.size > 0) {
            rerouteAllChartArrows(editor, rerouteIds, {useArc: true, arcOnly: true});
        }
        const fanInIds = collectFanInHubArrowIds(editor);
        if (fanInIds.size > 0) {
            rerouteAllChartArrows(editor, fanInIds, {useArc: true, arcOnly: true});
        }
        dedupeFanInArrowLabels(editor);
    }

    for (const mutation of otherMutations) {
        const op = mutation?.op;
        try {
            if (op === 'update_entity_card') {
                const entityRef = mutation.entityRef;
                const label = mutation.label;
                if (!entityRef || !label) {
                    throw new Error('update_entity_card requires entityRef and label');
                }
                const shapeId = entityShapeIds.get(entityRef);
                if (!shapeId) {
                    throw new Error(`Cannot update ${entityRef}: entity card missing on chart`);
                }
                const shape = editor.getShape?.(shapeId);
                if (!shape || shape.type !== ENTITY_CARD_SHAPE_TYPE) {
                    throw new Error(`Cannot update ${entityRef}: not an entity card`);
                }
                editor.updateShapes?.([{
                    id: shapeId,
                    type: ENTITY_CARD_SHAPE_TYPE,
                    props: {...shape.props, label},
                    meta: mergeAuthorshipMeta(shape.meta, AI_AUTHOR_UID),
                }]);
                applied += 1;
                results.push({ok: true, mutation});
            } else if (op === 'update_arrow') {
                const fromRef = mutation.fromEntityRef;
                const toRef = mutation.toEntityRef;
                if (!fromRef || !toRef) {
                    throw new Error('update_arrow requires fromEntityRef and toEntityRef');
                }
                const fromShapeId = entityShapeIds.get(fromRef);
                const toShapeId = entityShapeIds.get(toRef);
                if (!fromShapeId || !toShapeId) {
                    throw new Error(`Cannot update arrow ${fromRef} → ${toRef}: entity card missing on chart`);
                }
                const arrowId = findArrowBetweenShapes(editor, fromShapeId, toShapeId);
                if (!arrowId) {
                    throw new Error(`Cannot update arrow ${fromRef} → ${toRef}: connection missing on chart`);
                }
                const arrowShape = editor.getShape?.(arrowId);
                const arrowLabel = sanitizeArrowRelationLabel(
                    mutation.label || mutation.relation || null
                );
                editor.updateShapes?.([{
                    id: arrowId,
                    type: 'arrow',
                    props: {
                        ...arrowShape?.props,
                        richText: arrowLabelRichText(arrowLabel || ''),
                    },
                    meta: mergeAuthorshipMeta(arrowShape?.meta, AI_AUTHOR_UID),
                }]);
                applied += 1;
                results.push({ok: true, mutation});
            } else {
                throw new Error(`Unsupported mutation op: ${op || '(missing)'}`);
            }
        } catch (error) {
            const technical = error?.message || String(error);
            const message = formatChartErrorForUser(technical);
            failed.push({mutation, error: message});
            results.push({ok: false, mutation, error: message});
        }
    }

    const layoutHint = plan?.layout ?? null;
    if (shouldRelayoutAfterMutations(mutations, preserveLayout)) {
        layoutChartEntities(editor, entityShapeIds, mutations, layoutHint);
    }

    let hydration = null;
    if (cardMutations.length > 0) {
        const addedRefs = cardMutations
            .map((mutation) => normalizeEntityRef(mutation.entityRef))
            .filter(Boolean);
        // Enrich card metadata asynchronously — structural apply (cards, edges, layout) must not
        // block on /api/entities hydration (Wikidata rate limits can stall for minutes).
        hydration = Promise.resolve(hydrateEntityCardsFromKb(editor, addedRefs)).catch((error) => {
            console.warn('[applyChartMutations] Background KB hydration failed', error);
        });
    }

    return {
        applied,
        skipped,
        failed,
        results,
        existingEntityCountBefore: existingCardCount,
        addedEntityCount,
        addedArrowCount,
        hydration,
    };
}

/**
 * Empty-chart multi-card builds get a full relayout; incremental adds preserve positions.
 *
 * @param {number} existingCardCount
 * @param {Array<object>} cardAdds
 * @param {Array<object>} [_arrowMutations]
 * @returns {boolean}
 */
export function shouldPreserveLayout(existingCardCount, cardAdds, _arrowMutations) {
    if (existingCardCount === 0 && cardAdds.length > 1) {
        return false;
    }
    return true;
}

/**
 * @param {Array<object>} mutations
 * @param {boolean} preserveLayout
 * @returns {boolean}
 */
export function shouldRelayoutAfterMutations(mutations, preserveLayout = false) {
    if (preserveLayout) return false;
    const cardAdds = (mutations || []).filter((mutation) => mutation?.op === 'add_entity_card');
    if (cardAdds.length >= 2) return true;
    return isHubSpokeTopology(mutations);
}

/**
 * Merge planned arrow mutations with arrows already on the chart so layout
 * sees the full topology (not just the current apply batch).
 *
 * @param {import('tldraw').Editor} editor
 * @param {Map<string, string>} entityShapeIds
 * @param {Array<object>} mutations
 * @returns {Array<object>}
 */
function mergeLayoutMutations(editor, entityShapeIds, mutations) {
    const merged = [...(mutations || [])];
    const seen = new Set(
        merged
            .filter((mutation) => mutation?.op === 'add_arrow')
            .map((mutation) => `${mutation.fromEntityRef}\0${mutation.toEntityRef}`)
    );
    const shapeIdToRef = new Map([...entityShapeIds.entries()].map(([ref, id]) => [id, ref]));
    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        const bindings = getArrowBindings(editor, shape);
        const fromRef = shapeIdToRef.get(bindings?.start?.toId);
        const toRef = shapeIdToRef.get(bindings?.end?.toId);
        if (!fromRef || !toRef) continue;
        const key = `${fromRef}\0${toRef}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push({op: 'add_arrow', fromEntityRef: fromRef, toEntityRef: toRef});
    }
    return merged;
}

/**
 * True when one hub node participates in most edges (fan-in star).
 * @param {Array<object>} mutations
 * @returns {boolean}
 */
export function isHubSpokeTopology(mutations) {
    const arrows = (mutations || []).filter((mutation) => mutation?.op === 'add_arrow');
    if (arrows.length < 4) return false;

    const degree = new Map();
    for (const arrow of arrows) {
        if (arrow.fromEntityRef) {
            degree.set(arrow.fromEntityRef, (degree.get(arrow.fromEntityRef) || 0) + 1);
        }
        if (arrow.toEntityRef) {
            degree.set(arrow.toEntityRef, (degree.get(arrow.toEntityRef) || 0) + 1);
        }
    }

    let hubRef = null;
    let maxDegree = -1;
    for (const [ref, count] of degree) {
        if (count > maxDegree) {
            maxDegree = count;
            hubRef = ref;
        }
    }
    if (!hubRef || maxDegree < Math.ceil(arrows.length * 0.6)) return false;
    return arrows.every((arrow) => arrow.fromEntityRef === hubRef || arrow.toEntityRef === hubRef);
}

/**
 * @param {Array<object>} mutations
 * @param {string|null|undefined} layoutHint
 * @returns {'hub'|'hierarchy'}
 */
export function inferChartLayout(mutations, layoutHint) {
    if (isHubSpokeTopology(mutations)) {
        return 'hub';
    }
    if (layoutHint === 'hierarchy') {
        return 'hierarchy';
    }
    // Hub spreads satellites radially and keeps fan-in edge labels readable.
    return 'hub';
}

/**
 * @param {import('tldraw').Editor} editor
 * @param {Map<string, string>} entityShapeIds
 * @param {Array<object>} mutations
 * @param {string|null|undefined} layoutHint
 */
export function layoutChartEntities(editor, entityShapeIds, mutations, layoutHint) {
    const layoutMutations = mergeLayoutMutations(editor, entityShapeIds, mutations);
    const style = inferChartLayout(layoutMutations, layoutHint);
    if (style === 'hierarchy') {
        layoutHierarchyTopDown(editor, entityShapeIds, layoutMutations);
        resolveEntityCardOverlaps(editor);
        dedupeFanInArrowLabels(editor);
        rerouteAllChartArrows(editor, null, {useArc: true, preferStraight: true});
        return;
    }
    layoutChartHubSquare(editor);
}

/**
 * Re-layout every entity card on the chart and reroute all arrows.
 * Use to clean up an existing messy chart without re-adding entities.
 *
 * @param {import('tldraw').Editor} editor
 * @param {string|null|undefined} [layoutHint]
 */
export function tidyChartLayout(editor, layoutHint = null) {
    if (!editor) return;
    const entityShapeIds = indexEntityShapesByRef(editor);
    if (entityShapeIds.size < 2) return;
    const mutations = mergeLayoutMutations(editor, entityShapeIds, []);
    layoutChartEntities(editor, entityShapeIds, mutations, layoutHint);
}

/**
 * Top-down org layout: hub/root at top, members in rows below by tree depth.
 *
 * @param {import('tldraw').Editor} editor
 * @param {Map<string, string>} entityShapeIds
 * @param {Array<object>} mutations
 */
export function layoutHierarchyTopDown(editor, entityShapeIds, mutations) {
    if (!editor || !entityShapeIds || entityShapeIds.size < 2) return;

    const arrows = (mutations || []).filter((mutation) => mutation?.op === 'add_arrow');
    const parent = new Map();
    const children = new Map();
    const fanIn = new Map();

    for (const arrow of arrows) {
        const from = arrow.fromEntityRef;
        const to = arrow.toEntityRef;
        if (!from || !to) continue;
        fanIn.set(to, (fanIn.get(to) || 0) + 1);
        const relation = String(arrow.label || arrow.relation || '').toLowerCase();
        if (relation.includes('member')
            || relation.includes('leader')
            || relation.includes('subordinate')
            || relation.includes('reports')
            || relation.includes('affiliated')) {
            parent.set(from, to);
            if (!children.has(to)) children.set(to, []);
            children.get(to).push(from);
        }
    }

    let rootRef = null;
    let bestFanIn = -1;
    for (const [ref, count] of fanIn) {
        if (count > bestFanIn && entityShapeIds.has(ref)) {
            bestFanIn = count;
            rootRef = ref;
        }
    }
    if (!rootRef) {
        for (const ref of entityShapeIds.keys()) {
            if (!parent.has(ref)) {
                rootRef = ref;
                break;
            }
        }
    }
    if (!rootRef) {
        rootRef = entityShapeIds.keys().next().value;
    }

    const level = new Map([[rootRef, 0]]);
    const queue = [rootRef];
    while (queue.length > 0) {
        const node = queue.shift();
        for (const child of children.get(node) || []) {
            if (!level.has(child)) {
                level.set(child, level.get(node) + 1);
                queue.push(child);
            }
        }
    }

    const inTree = new Set([rootRef]);
    const markTree = (ref) => {
        for (const child of children.get(ref) || []) {
            if (!inTree.has(child)) {
                inTree.add(child);
                markTree(child);
            }
        }
    };
    markTree(rootRef);

    let centerX = 400;
    let topY = 120;
    const container = editor.getContainer?.();
    if (container && typeof editor.screenToPage === 'function') {
        const rect = container.getBoundingClientRect();
        const center = editor.screenToPage({
            x: rect.left + rect.width / 2,
            y: rect.top + Math.min(rect.height * 0.2, 160),
        });
        centerX = center.x;
        topY = center.y;
    }

    const colStep = ENTITY_CARD_WIDTH + GRID_GAP;
    const rowGap = PLACEMENT_CARD_HEIGHT + GRID_GAP * 3;
    const xSlots = new Map();
    let nextLeafSlot = 0;

    const assignTreeSlots = (ref) => {
        const kids = (children.get(ref) || []).filter((child) => entityShapeIds.has(child));
        if (kids.length === 0) {
            xSlots.set(ref, nextLeafSlot);
            nextLeafSlot += 1;
            return xSlots.get(ref);
        }
        const childSlots = kids.map(assignTreeSlots);
        const slot = (Math.min(...childSlots) + Math.max(...childSlots)) / 2;
        xSlots.set(ref, slot);
        return slot;
    };
    assignTreeSlots(rootRef);

    let minSlot = Infinity;
    let maxSlot = -Infinity;
    for (const slot of xSlots.values()) {
        minSlot = Math.min(minSlot, slot);
        maxSlot = Math.max(maxSlot, slot);
    }
    const treeWidth = (maxSlot - minSlot + 1) * colStep;
    const treeOriginX = centerX - treeWidth / 2;

    const placeCard = (ref, x, y) => {
        const shapeId = entityShapeIds.get(ref);
        if (!shapeId) return;
        const cardRect = shapeRect(editor, shapeId);
        const w = cardRect?.w || ENTITY_CARD_WIDTH;
        editor.updateShapes?.([{
            id: shapeId,
            type: ENTITY_CARD_SHAPE_TYPE,
            x: x - w / 2,
            y,
        }]);
    };

    for (const [ref, slot] of xSlots) {
        const x = treeOriginX + (slot - minSlot) * colStep + colStep / 2;
        const y = topY + (level.get(ref) || 0) * rowGap;
        placeCard(ref, x, y);
    }

    let maxTreeLevel = 0;
    for (const ref of inTree) {
        maxTreeLevel = Math.max(maxTreeLevel, level.get(ref) || 0);
    }

    const orphans = [...entityShapeIds.keys()].filter((ref) => !inTree.has(ref));
    if (orphans.length > 0) {
        const orphanY = topY + (maxTreeLevel + 2) * rowGap;
        const orphanSubRows = [];
        for (let i = 0; i < orphans.length; i += MAX_HIERARCHY_ROW_WIDTH) {
            orphanSubRows.push(orphans.slice(i, i + MAX_HIERARCHY_ROW_WIDTH));
        }
        orphanSubRows.forEach((rowRefs, subRowIndex) => {
            const rowWidth = rowRefs.length * colStep;
            rowRefs.forEach((ref, index) => {
                const x = centerX - rowWidth / 2 + index * colStep + colStep / 2;
                placeCard(ref, x, orphanY + subRowIndex * rowGap);
            });
        });
    }
}

/**
 * Reposition entity cards so the most-connected node (hub) sits near the viewport center
 * and satellites form a ring around it.
 *
 * @param {import('tldraw').Editor} editor
 * @param {Map<string, string>} entityShapeIds entityRef → shapeId
 * @param {Array<object>} mutations plan mutations (add_arrow)
 */
export function layoutHubCentric(editor, entityShapeIds, mutations) {
    if (!editor || !entityShapeIds || entityShapeIds.size < 2) return;

    const degree = new Map();
    for (const mutation of mutations || []) {
        if (mutation?.op !== 'add_arrow') continue;
        const from = mutation.fromEntityRef;
        const to = mutation.toEntityRef;
        if (from) degree.set(from, (degree.get(from) || 0) + 1);
        if (to) degree.set(to, (degree.get(to) || 0) + 1);
    }
    let hubRef = null;
    let bestDegree = -1;
    for (const [ref, count] of degree) {
        if (count > bestDegree && entityShapeIds.has(ref)) {
            bestDegree = count;
            hubRef = ref;
        }
    }
    if (!hubRef) {
        hubRef = entityShapeIds.keys().next().value;
    }
    const hubShapeId = entityShapeIds.get(hubRef);
    if (!hubShapeId) return;

    let centerX = 400;
    let centerY = 300;
    const container = editor.getContainer?.();
    if (container && typeof editor.screenToPage === 'function') {
        const rect = container.getBoundingClientRect();
        const center = editor.screenToPage({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
        });
        centerX = center.x;
        centerY = center.y;
    }

    const hubRect = shapeRect(editor, hubShapeId);
    if (hubRect) {
        editor.updateShapes?.([{
            id: hubShapeId,
            type: ENTITY_CARD_SHAPE_TYPE,
            x: centerX - hubRect.w / 2,
            y: centerY - hubRect.h / 2,
        }]);
    }

    const satellites = [...entityShapeIds.entries()].filter(([ref]) => ref !== hubRef);
    if (satellites.length === 0) return;

    const minArcLength = satellites.length * (ENTITY_CARD_WIDTH * 0.75);
    let radius = Math.max(
        ENTITY_CARD_WIDTH + GRID_GAP * 2,
        280,
        minArcLength / (2 * Math.PI),
    );
    const placeSatellites = () => {
        satellites.forEach(([, shapeId], index) => {
            const angle = (2 * Math.PI * index) / satellites.length - Math.PI / 2;
            const cardRect = shapeRect(editor, shapeId);
            const w = cardRect?.w || ENTITY_CARD_WIDTH;
            const h = cardRect?.h || PLACEMENT_CARD_HEIGHT;
            editor.updateShapes?.([{
                id: shapeId,
                type: ENTITY_CARD_SHAPE_TYPE,
                x: centerX + Math.cos(angle) * radius - w / 2,
                y: centerY + Math.sin(angle) * radius - h / 2,
            }]);
        });
    };
    for (let attempt = 0; attempt < 12; attempt += 1) {
        placeSatellites();
        if (measureCardCardOverlaps(editor) === 0) break;
        radius += 48;
    }
    resolveEntityCardOverlaps(editor);
}

export default applyChartMutations;
